import type { DeviceStyle, NodeV2 } from '@vedalib/editor'
import type { ElementSchemaType, ImageSchemaType } from '@vedalib/editor/lib/brand'
import type { ElementSchemaDeviceType } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'

import type { ElementStyleValue } from 'services/Branding/types'

import type { DeviceMode } from './enums'
import type { Block, IProjectContext } from './types'

export const stateLens = R.lensProp<IProjectContext, 'state'>('state')

export const courseLens = R.compose(stateLens, R.lensProp('course'))

// state blocks
export const blocksLens = R.compose(stateLens, R.lensProp('blocks'))
export const blockLens = (id = '') => R.compose(blocksLens, R.lensProp(id || ''))

// state elements
export const elementsLens = R.compose(stateLens, R.lensProp('elements'))
export const elementLens = (id = '') => R.compose(elementsLens, R.lensProp(id))

// state sections
export const sectionsLens = R.compose(stateLens, R.lensProp('sections'))
export const sectionLens = (id = '') => R.compose(sectionsLens, R.lensProp(id))

// state editor
export const editorLens = R.compose(stateLens, R.lensProp('editor'))
export const settingsLens = R.compose(editorLens, R.lensProp('settings'))
export const toolbarLens = R.compose(editorLens, R.lensProp('toolbar'))
export const navigationLens = R.compose(editorLens, R.lensProp('navBar'))

// BlockV2
export const blockElementsLens = R.lensProp<Block, 'elements'>('elements')
export const blockSchemaLens = R.lensProp<Block, 'schema'>('schema')
export const nodesLens = R.compose(blockSchemaLens, R.lensProp('nodes'))
export const nodeLens = (id = '') => R.compose(nodesLens, R.lensProp(id)) as R.Lens<Block, NodeV2>

// Elements
const deviceElementStyleHelperLens = <S extends ElementSchemaType>(device: DeviceMode) =>
  R.lens<ElementSchemaDeviceType<S>, ElementStyleValue<S>>(
    (style) => (style[device] || {}) as ElementStyleValue<S>,
    (value, style) => ({ ...style, [device]: value }),
  )
export const elementDataLens = (id = '') => R.compose(blockElementsLens, R.lensProp(id))
export const elementValueLens = (id = '') => R.compose(elementDataLens(id), R.lensProp('value'))
export const elementValueItemsLens = (id = '') =>
  R.compose(elementValueLens(id), R.lensProp('items')) as R.Lens<Block, { value: string }[]>
export const elementValueItemLens = (id = '') =>
  R.compose(elementValueLens(id), R.lensProp('item')) as R.Lens<Block, { value: string }>
export const elementFullStyleLens = (id = '') =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  R.compose(elementDataLens(id), R.lensProp<any, any>('style'))
export const elementStyleLens = <S extends ElementSchemaType>(id = '', device: DeviceMode) =>
  R.compose(elementFullStyleLens(id), deviceElementStyleHelperLens<S>(device))
export const imageLens = (id = '', device: DeviceMode) =>
  R.compose(elementStyleLens<ImageSchemaType>(id, device), R.lensProp('image'))
export const imageObjectFitLens = (id = '', device: DeviceMode) =>
  R.compose(imageLens(id, device), R.lensProp('objectFit'))

// NodeV2
export const childrenLens = (id = '') => R.compose(nodeLens(id), R.lensProp('children'))
export const doneLens = (id = '') => R.compose(nodeLens(id), R.lensProp('isDone'))
export const labelLens = (id = '') => R.compose(nodeLens(id), R.lensProp('label'))
export const repeatableLens = (id = '') => R.compose(nodeLens(id), R.lensProp('repeatable'))
const deviceStyleHelperLens = (device: DeviceMode) =>
  R.lens(
    (style: DeviceStyle) => style?.[device] || {},
    (value, style) => ({ ...style, [device]: value }),
  )
export const fullStyleLens = (id = '') => R.compose(nodeLens(id), R.lensProp('style'))
export const styleLens = (id = '', device: DeviceMode) =>
  R.compose(fullStyleLens(id), deviceStyleHelperLens(device))

// NodeV2Styles Geometry
export const positionLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('position'))
export const paddingLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('padding'))
export const borderRadiusLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('borderRadius'))
export const widthLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('width'))
export const heightLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('height'))
export const minWidthLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('minWidth'))
export const maxWidthLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('maxWidth'))
export const minHeightLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('minHeight'))
export const maxHeightLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('maxHeight'))

// NodeV2Styles Grid
export const gridAreaLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('gridArea'))
export const gridRowsLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('gridRows'))
export const gridRowsGapLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('gridRowsGap'))
export const gridColumnsLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('gridColumns'))
export const gridColumnsGapLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('gridColumnsGap'))
export const zIndexLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('zIndex'))

// NodeV2Styles Flex
export const flexAlignItemsLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexAlignItems'))
export const flexJustifyContentLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexJustifyContent'))
export const flexDirectionLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexDirection'))
export const flexWrapLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexWrap'))
export const flexGapLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexGap'))
export const flexFlowLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexFlow'))
export const flexShrinkLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexShrink'))
export const flexGrowLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('flexGrow'))
export const alignSelfLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('alignSelf'))

// NodeV2Styles Absolute
// export const absoluteTypeLens = (id = '', device: DeviceMode) =>
//   R.compose(styleLens(id, device), R.lensProp('absoluteType'))
export const coordsLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('coords'))

// NodeV2Styles Common
export const displayLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('display'))
export const hiddenLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('hidden'))
export const opacityLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('opacity'))
export const boxShadowLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('boxShadow'))
export const backdropFilterLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('backdropFilter'))
export const filterLens = (id = '', device: DeviceMode) =>
  R.compose(styleLens(id, device), R.lensProp('filter'))
